import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Home, Store, ShoppingCart, ClipboardList, FileText, LogOut, Bell } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/retailer", icon: Home, label: "Home", end: true },
  { to: "/retailer/marketplace", icon: Store, label: "Market" },
  { to: "/retailer/cart", icon: ShoppingCart, label: "Cart" },
  { to: "/retailer/orders", icon: ClipboardList, label: "Orders" },
  { to: "/retailer/invoices", icon: FileText, label: "Invoices" },
];

export default function RetailerLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { logout, profile } = useAuth();
  const [count, setCount] = useState(0);

  const loadCart = async () => {
    try { const { data } = await api.get("/cart"); setCount(data.count); } catch {}
  };
  useEffect(() => { loadCart(); }, [loc.pathname]);

  const active = (to, end) => (end ? loc.pathname === to : loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-sgrey pb-20 md:pb-0">
      <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <Logo className="scale-90 origin-left" />
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={cn("relative rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active(n.to, n.end) ? "bg-forest text-white" : "text-ink/70 hover:bg-psage")}>
                {n.label}
                {n.label === "Cart" && count > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-olive text-[10px] text-white num">{count}</span>
                )}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/retailer/subscription" data-testid="nav-plans" className="hidden text-sm font-medium text-forest hover:underline md:block">Plans</Link>
            <Link to="/retailer/profile" data-testid="nav-profile" className="hidden text-sm font-medium text-forest hover:underline md:block">
              {profile?.shop_name || "My Shop"}
            </Link>
            <Button data-testid="logout-btn" variant="ghost" size="icon" className="text-ink/60 hover:bg-psage" onClick={() => { logout(); nav("/"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6"><Outlet /></main>

      <nav className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-5 border-t border-border bg-card md:hidden">
        {NAV.map((n) => (
          <Link key={n.to} to={n.to} data-testid={`bottomnav-${n.label.toLowerCase()}`}
            className={cn("flex flex-col items-center gap-0.5 py-2.5 text-[11px]",
              active(n.to, n.end) ? "text-forest" : "text-ink/50")}>
            <div className="relative">
              <n.icon className="h-5 w-5" />
              {n.label === "Cart" && count > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-olive text-[9px] text-white num">{count}</span>
              )}
            </div>
            {n.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
